import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import _ from 'lodash';
import { IApiBranch, IApiContact } from 'src/app/shared/modules/graphql/types/types';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { InvestigationSelectContactCompanyModalComponent } from '../investigation-select-contact-company-modal/investigation-select-contact-company-modal.component';

export interface IInvestigationBillToItem {
  id?: string;
  BillTo?: IApiContact;
  BillToBranch?: IApiBranch;
  BillToId?: string;
  BillToBranchId?: string;
  percentage?: number;
  isPrimary?: boolean;
  claimNumber?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-investigation-additional-bill-to',
  templateUrl: './investigation-additional-bill-to.component.html',
  styleUrls: ['./investigation-additional-bill-to.component.scss']
})
export class InvestigationAdditionalBillToComponent implements OnChanges {
  @Input() public investigationBillTos: IInvestigationBillToItem[] = [];
  @Input() public mainBillTo?: IApiContact;
  @Input() public mainBillToBranch?: IApiBranch;
  @Input() public mainClaimNumber?: string;
  // Locks add, remove and update (e.g. once a successful invoice exists)
  @Input() public disabled = false;
  @Input() public disabledMessage?: string;

  @Output() public investigationBillTosChange = new EventEmitter<IInvestigationBillToItem[]>();
  @Output() public changed = new EventEmitter<IInvestigationBillToItem[]>();

  constructor(
    private dialogService: DialogService,
    private notifications: NotificationsService
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.mainBillTo || changes.mainBillToBranch || changes.mainClaimNumber) {
      if (this.mainBillTo && this.mainBillTo.id) {
        if (this.investigationBillTos && this.investigationBillTos.length > 0) {
          const primaryItem = this.investigationBillTos.find(item => item.isPrimary);
          if (primaryItem) {
            primaryItem.BillTo = this.mainBillTo;
            primaryItem.BillToBranch = this.mainBillToBranch || null;
            primaryItem.BillToId = this.mainBillTo.id;
            primaryItem.BillToBranchId = this.mainBillToBranch?.id || null;
            primaryItem.claimNumber = this.mainClaimNumber || null;
            this.emitChange();
          }
        }
      } else {
        // Main Bill To removed -> reset entire additional bill to section
        if (this.investigationBillTos && this.investigationBillTos.length > 0) {
          this.investigationBillTos = [];
          this.emitChange();
        }
      }
    }
  }

  public get totalPercentage(): number {
    if (!this.investigationBillTos?.length) return 0;
    const total = this.investigationBillTos.reduce((sum, item) => sum + (Number(item.percentage) || 0), 0);
    return Math.round(total * 100) / 100;
  }

  public get isTotalInvalid(): boolean {
    if (!this.investigationBillTos?.length) return false;
    return this.totalPercentage !== 100;
  }

  public addInvestigationBillTo(): void {
    if (this.disabled) return;
    if (!this.mainBillTo || !this.mainBillTo.id) {
      this.notifications.alert("Please select Client/Bill To in Investigation Information first.");
      return;
    }

    if (!this.investigationBillTos || this.investigationBillTos.length === 0) {
      // First click: Add Primary entry + 1 Blank entry
      const primaryItem: IInvestigationBillToItem = {
        id: null,
        BillTo: this.mainBillTo,
        BillToBranch: this.mainBillToBranch || null,
        BillToId: this.mainBillTo.id,
        BillToBranchId: this.mainBillToBranch?.id || null,
        percentage: null,
        isPrimary: true,
        claimNumber: this.mainClaimNumber || null,
      };

      const blankItem: IInvestigationBillToItem = {
        id: null,
        BillTo: null,
        BillToBranch: null,
        BillToId: null,
        BillToBranchId: null,
        percentage: null,
        isPrimary: false,
        claimNumber: null,
      };

      this.investigationBillTos = [primaryItem, blankItem];
    } else {
      // Subsequent clicks: Add 1 Blank entry
      this.investigationBillTos.push({
        id: null,
        BillTo: null,
        BillToBranch: null,
        BillToId: null,
        BillToBranchId: null,
        percentage: null,
        isPrimary: false,
        claimNumber: null,
      });
    }

    this.emitChange();
  }

  public removeInvestigationBillTo(index: number): void {
    if (this.disabled) return;
    if (this.investigationBillTos && index >= 0 && index < this.investigationBillTos.length) {
      if (this.investigationBillTos[index]?.isPrimary) {
        return; // Primary cannot be removed
      }

      this.investigationBillTos.splice(index, 1);

      // If only primary item remains (or no non-primary items), clear whole section
      const hasNonPrimary = this.investigationBillTos.some(item => !item.isPrimary);
      if (!hasNonPrimary) {
        this.investigationBillTos = [];
      }

      this.emitChange();
    }
  }

  public selectBillTo(index: number): void {
    if (this.disabled) return;
    if (this.investigationBillTos[index]?.isPrimary) {
      return; // Primary Bill To cannot be changed from here
    }

    const dialog: DialogRef = this.dialogService.open({
      content: InvestigationSelectContactCompanyModalComponent,
      width: '80%',
      preventAction: (ev) => {
        return ev !== ('closed' as any);
      },
    });
    const dialogInstance = dialog.content.instance as InvestigationSelectContactCompanyModalComponent;
    dialogInstance.data = {
      partyAdd: true,
    };
    dialog.result
      .pipe(
        filter((v) => !_.isEmpty(v)),
        this.notifications.snackbarErrorPipe("There was an error setting the BillTo contact. Please try again")
      )
      .subscribe(({ contact, branch = null }: any) => {
        if (this.investigationBillTos[index]) {
          this.investigationBillTos[index].BillTo = contact;
          this.investigationBillTos[index].BillToBranch = branch;
          this.investigationBillTos[index].BillToId = contact?.id || null;
          this.investigationBillTos[index].BillToBranchId = branch?.id || null;
          this.emitChange();
        }
      });
  }

  public removeBillTo(index: number): void {
    if (this.disabled) return;
    if (this.investigationBillTos[index] && !this.investigationBillTos[index].isPrimary) {
      this.investigationBillTos[index].BillTo = null;
      this.investigationBillTos[index].BillToBranch = null;
      this.investigationBillTos[index].BillToId = null;
      this.investigationBillTos[index].BillToBranchId = null;
      this.emitChange();
    }
  }

  public onPercentageChange(): void {
    this.emitChange();
  }

  public emitChange(): void {
    this.investigationBillTosChange.emit(this.investigationBillTos);
    this.changed.emit(this.investigationBillTos);
  }
}
